/* eslint-disable react/jsx-no-bind */

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as PropTypes from 'prop-types'
import { withStyles, Paper, Tabs, Tab, Container } from '@material-ui/core'
import axios from 'axios'
import clsx from 'clsx'

import i18n from 'support/i18n'
import { USERS_LIMIT_CONSTANT } from 'common_constants/Dashboard'
import { FIRST_LIMIT_CONSTANT } from 'common_constants/Ideas'

import { dashboardTabs, typesViewAs } from './Constants'
import styles from './View.Style'

import Activity from './components/activity/View'
import ActivityIdeas from './components/activity_ideas/View'
import ActivityPeople from './components/activity_people/View'
import IntervalDatesPicker from './components/interval_dates_picker/View'
import ReportSelector from './components/report_selector/View'
import FilterSection from './components/FilterSection'
import ChallengeFilterReport from './components/ChallengeFilterReport'
import EvaluatorReportFilter from './components/EvaluatorReportFilter'
import ChallengeAllReport from './components/ChallengeAllReport'


const ACTIVE_CHALLENGES = 'ACTIVE'

const comparisonIdeasTableHeaders = [
  {
    text: i18n.t('DASHBOARD.IDEAS_RAISING'),
    icon: 'earn-money',
    borderColor: '#ffcd40',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_SUPPORTED'),
    icon: 'success',
    borderColor: '#d19600',
  },
  {
    text: i18n.t('INVESTMENTS.ROUND_CLOSED'),
    icon: 'success',
    borderColor: '#0070a7',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_APPROVED'),
    icon: 'approved',
    borderColor: '#0097e2',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_PILOT'),
    icon: 'prototype',
    borderColor: '#63ffe4',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_IMPLEMENTED'),
    icon: 'ranking',
    borderColor: '#00d24c',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_DISCARDED'),
    icon: 'close',
    borderColor: '#fa5d7f',
  },
]

const comparisonPrivateIdeasTableHeaders = [
  {
    text: i18n.t('INVESTMENTS.ROUND_CLOSED'),
    icon: 'success',
    borderColor: '#0070a7',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_APPROVED'),
    icon: 'approved',
    borderColor: '#0097e2',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_PILOT'),
    icon: 'prototype',
    borderColor: '#63ffe4',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_IMPLEMENTED'),
    icon: 'ranking',
    borderColor: '#00d24c',
  },
  {
    text: i18n.t('DASHBOARD.IDEAS_DISCARDED'),
    icon: 'close',
    borderColor: '#fa5d7f',
  },
]

const privateStatusFilterOptions = [
  { value: 'ROUND_CLOSED', text: 'IDEA_STATUS.ROUND_CLOSED' },
  { value: 'APPROVED_BY_IT', text: 'IDEA_STATUS.APPROVED_BY_IT' },
  { value: 'PILOT_ACTIVE', text: 'IDEA_STATUS.PILOT_ACTIVE' },
  { value: 'IMPLEMENTED', text: 'IDEA_STATUS.IMPLEMENTED' },
  { value: 'DISCARDED', text: 'IDEA_STATUS.DISCARDED' },
]

const statusFilterOptions = [
  { value: 'PENDING_VALIDATION', text: 'IDEA_STATUS.PENDING_VALIDATION' },
  { value: 'PENDING_SUPPORT', text: 'IDEA_STATUS.PENDING_SUPPORT' },
  { value: 'SUPPORTED', text: 'IDEA_STATUS.SUPPORTED' },
  { value: 'ROUND_CLOSED', text: 'IDEA_STATUS.ROUND_CLOSED' },
  { value: 'APPROVED_BY_IT', text: 'IDEA_STATUS.APPROVED_BY_IT' },
  { value: 'PILOT_ACTIVE', text: 'IDEA_STATUS.PILOT_ACTIVE' },
  { value: 'IMPLEMENTED', text: 'IDEA_STATUS.IMPLEMENTED' },
  { value: 'DISCARDED', text: 'IDEA_STATUS.DISCARDED' },
]

const getFileNameFromContentDisposition = (
  contentDisposition,
  fallback = 'report.csv'
) => {
  if (!contentDisposition) {
    return fallback
  }

  // Supports RFC 5987: filename*=UTF-8''report.csv
  const encodedMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;\n]+)/i
  )

  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim())
    } catch (error) {
      return encodedMatch[1].trim()
    }
  }

  const filenameMatch = contentDisposition.match(
    /filename\s*=\s*"?([^";\n]+)"?/i
  )

  return filenameMatch?.[1]?.trim() || fallback
}


const Dashboard = props => {
  const {
    classes,
    activeTab,
    isLoading,
    isActivitySeriesLoading,
    isIdeasSeriesLoading,
    isPrivateIdeasLoading,
    activity,
    ideas,
    people,
    privateIdeas,
    showPrivateTab,
    navigateToUserProfile,
    domainCreationDate,
    usersParticipation,
    isUsersParticipationSeriesLoading,
    onSendPeopleReport,
    ideasTableBottom,
    ideasTableBottomTotal,
    ideasTableBottomIsLoading,
    navigateToIdea,
    organizations,
    groups,
    strategics,
    challenges,
    types,
    ideaTablePagination,
    csrfToken,
    userEmail,
    activityFromDate,
    activityToDate,
    ideasFromDate,
    ideasToDate,
    peopleFromDate,
    peopleToDate,
    privateIdeasFromDate,
    privateIdeasToDate,

    peopleUserFilter,
    userTableFilter,

    onChangeTab,
    onChangeDate,

    getActivityComparisonTable,
    getIdeasComparisonTable,
    getPeopleComparisonTable,
    getPrivateIdeasComparisonTable,
    getSystemAccesses,
    getIdeasSeries,
    getPrivateIdeasSeries,
    getUsers,
    getUserParticipation,
    getPrivateIdeas,
    getIdeas,
    getActiveOrganizations,
    getGroups,
    getActiveGoals,
    getActiveChallenges,
    getActiveTypes,

    setUsersTableFilter,
    setPrivateIdeaTableFilter,
    setIdeaTableFilter,
    requestGetChallengeWithStatusFilterFn,
    getChallengeWithStatusFilterList,
    getChallengeWithStatusFilterListIsLoading,
    isHackathonEnable,
  } = props

  const [isDownloading, setIsDownloading] = useState(false)
  const [isChallengeAllReportDownloading, setIsChallengeAllReportDownloading] = useState(false)
  const [isIdeaDownloading, setIsIdeaDownloading] = useState(false)
  const [isEvaluatorDownloading, setIsEvaluatorDownloading] = useState(false)
  const [isChallengeLoading,setIsChallengeLoading] = useState(false)

const [challengeFilters, setChallengeFilters] = useState({
  challengeStatus: [],
  startDate: "",
  endDate: "",
});


  const userParticipationOffsetRef = useRef(0)
  const isMountedRef = useRef(true)

  /*
   * Prevent the pagination effect from treating the first response as a
   * subsequent "load more" response.
   */
  const previousParticipationSeriesRef = useRef(
    usersParticipation.series?.[0]
  )

  const { fromDate, toDate } = useMemo(() => {
    switch (activeTab) {
      case dashboardTabs.IDEAS:
        return {
          fromDate: ideasFromDate,
          toDate: ideasToDate,
        }

      case dashboardTabs.PEOPLE:
        return {
          fromDate: peopleFromDate,
          toDate: peopleToDate,
        }

      case dashboardTabs.PRIVATE_IDEAS:
        return {
          fromDate: privateIdeasFromDate,
          toDate: privateIdeasToDate,
        }

      case dashboardTabs.ACTIVITY:
      default:
        return {
          fromDate: activityFromDate,
          toDate: activityToDate,
        }
    }
  }, [
    activeTab,
    activityFromDate,
    activityToDate,
    ideasFromDate,
    ideasToDate,
    peopleFromDate,
    peopleToDate,
    privateIdeasFromDate,
    privateIdeasToDate,
  ])

  const handleChange = useCallback(
    (event, newValue) => {
      onChangeTab(newValue)
    },
    [onChangeTab]
  )

  const handleDatesChange = useCallback(
    datesObject => {
      onChangeDate(activeTab, datesObject)
    },
    [activeTab, onChangeDate]
  )

  const handleDateChange = useCallback(
    (date, isFrom = false) => {
      handleDatesChange(
        isFrom
          ? { fromDate: date }
          : { toDate: date }
      )
    },
    [handleDatesChange]
  )

  const handleSelectFromDate = useCallback(
    date => {
      handleDateChange(date, true)
    },
    [handleDateChange]
  )

  const handleSelectToDate = useCallback(
    date => {
      handleDateChange(date, false)
    },
    [handleDateChange]
  )

  const handleViewAsClick = useCallback(
    typeOfView => {
      const shouldReloadUsers =
        typeOfView === typesViewAs.GRID ||
        typeOfView === typesViewAs.LIST

      if (shouldReloadUsers) {
        getUsers(peopleUserFilter)
      }
    },
    [getUsers, peopleUserFilter]
  )

  const handleFilter = useCallback(
    filter => {
      setUsersTableFilter(filter)
      getUsers(filter)
    },
    [getUsers, setUsersTableFilter]
  )

  const handleFilterByText = useCallback(
    value => {
      const { filterType, rowsPerPage } = userTableFilter

      handleFilter({
        name: value,
        filterType,
        page: 0,
        rowsPerPage,
      })
    },
    [handleFilter, userTableFilter]
  )

  const handleFilterByType = useCallback(
    value => {
      const { name, rowsPerPage } = userTableFilter

      handleFilter({
        name,
        filterType: value,
        page: 0,
        rowsPerPage,
      })
    },
    [handleFilter, userTableFilter]
  )

  const handlePeoplePageChange = useCallback(
    (page, rowsPerPage) => {
      const { filterType, name } = userTableFilter

      handleFilter({
        name,
        filterType,
        page,
        rowsPerPage,
      })
    },
    [handleFilter, userTableFilter]
  )

  const handlePrivateIdeaPageChange = useCallback(
    (page, rowsPerPage, filters = {}) => {
      setPrivateIdeaTableFilter({
        page,
        rowsPerPage,
      })

      getPrivateIdeas({
        ...filters,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })
    },
    [getPrivateIdeas, setPrivateIdeaTableFilter]
  )

  const handleIdeaPageChange = useCallback(
    (page, rowsPerPage, filters = {}) => {
      setIdeaTableFilter({
        page,
        rowsPerPage,
      })

      getIdeas({
        ...filters,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })
    },
    [getIdeas, setIdeaTableFilter]
  )

  const downloadCsv = useCallback(
    async (type = 'idea') => {
      if (isDownloading) {
        return
      }

      setIsDownloading(true)

      try {
        const response = await axios.get(
          `/api/v1/innovate/report/${encodeURIComponent(type)}/csv`,
          {
            params: {
              _nxt_csrf: csrfToken,
            },
            responseType: 'blob',
          }
        )

        const contentType =
          response.headers['content-type'] || 'text/csv;charset=utf-8'

        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: contentType })

        const filename = getFileNameFromContentDisposition(
          response.headers['content-disposition'],
          `${type}-report.csv`
        )

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        try {
          link.href = url
          link.download = filename
          link.style.display = 'none'

          document.body.appendChild(link)
          link.click()
        } finally {
          link.remove()
          window.URL.revokeObjectURL(url)
        }
      } catch (error) {
        console.error('CSV download failed:', error)
      } finally {
        if (isMountedRef.current) {
          setIsDownloading(false)
        }
      }
    },
    [csrfToken, isDownloading]
  )


const downloadChallengeAllCsv = useCallback(
    async (type = 'challenges') => {
      if (isChallengeAllReportDownloading) {
        return
      }

      setIsChallengeAllReportDownloading(true)

      try {
        const response = await axios.get(
          `/api/v1/innovate/report/${encodeURIComponent(type)}/csv`,
          {
            params: {
              _nxt_csrf: csrfToken,
            },
            responseType: 'blob',
          }
        )

        const contentType =
          response.headers['content-type'] || 'text/csv;charset=utf-8'

        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: contentType })

        const filename = getFileNameFromContentDisposition(
          response.headers['content-disposition'],
          `${type}-report.csv`
        )

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        try {
          link.href = url
          link.download = filename
          link.style.display = 'none'

          document.body.appendChild(link)
          link.click()
        } finally {
          link.remove()
          window.URL.revokeObjectURL(url)
        }
      } catch (error) {
        console.error('CSV download failed:', error)
      } finally {
        if (isMountedRef.current) {
          setIsChallengeAllReportDownloading(false)
        }
      }
    },
    [csrfToken, isChallengeAllReportDownloading]
  )

const downloadIdeaReport = useCallback(
  async filterPayload => {
    if (isIdeaDownloading) return;

    setIsIdeaDownloading(true);

    const {
      ideaStatus = [],
      market = [],
      startDate,
      endDate,
    } = filterPayload || {};

    const params = new URLSearchParams();

    if (ideaStatus.length) {
      ideaStatus.forEach(status =>
        params.append('ideaStatus', status)
      );
    }

    if (market.length) {
      market.forEach(item =>
        params.append('market', item)
      );
    }

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    params.append('_nxt_csrf', csrfToken);

    const url = `/api/v1/innovate/report/ideas/filter/csv?${params.toString()}`;

    console.log('CSV URL:', url);

    try {
      const response = await axios.get(url, {
        responseType: 'blob',
      });

      const contentType =
        response.headers['content-type'] ||
        'text/csv;charset=utf-8';

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: contentType,
            });

      const filename =
        getFileNameFromContentDisposition(
          response.headers['content-disposition'],
          'idea-report.csv'
        );

      const downloadUrl =
        window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      try {
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }

    } catch (error) {
      console.error('CSV download failed:', error);
    } finally {
      if (isMountedRef.current) {
        setIsIdeaDownloading(false);
      }
    }
  },
  [csrfToken, isDownloading]
);

const downloadChallengeReport = useCallback(
  async (filterPayload) => {
    if (isChallengeLoading) return;

    setIsChallengeLoading(true);

    const { challengeList = [] } = filterPayload || {};

    const formData = new URLSearchParams();

challengeList.forEach(id => {
  formData.append('challengeList', id);
});

formData.append('_nxt_csrf', csrfToken);


 try {
const response = await axios.post(
  '/api/v1/innovate/report/selectedChallenge/fetchIdea/csv',
  formData,
  {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    responseType: 'blob',
  }
);

   

      const contentType =
        response.headers['content-type'] ||
        'text/csv;charset=utf-8';

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const filename = getFileNameFromContentDisposition(
        response.headers['content-disposition'],
        'idea-report.csv'
      );

      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      try {
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
      } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('CSV download failed:', error);
    } finally {
      if (isMountedRef.current) {
        setIsChallengeLoading(false);
      }
    }
  },
  [csrfToken, isChallengeLoading]
);


const downloadEvaluatorReport = useCallback(
  async filterPayload => {
    if (isEvaluatorDownloading) return;

    setIsEvaluatorDownloading(true);
    const {
      startDate,
      endDate,
    } = filterPayload || {};

    const params = new URLSearchParams();

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    params.append('_nxt_csrf', csrfToken);

    const url = `/api/v1/innovate/report/evaluators/datefilter/csv?${params.toString()}`;

    console.log('CSV URL:', url);

    try {
      const response = await axios.get(url, {
        responseType: 'blob',
      });

      const contentType =
        response.headers['content-type'] ||
        'text/csv;charset=utf-8';

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: contentType,
            });

      const filename =
        getFileNameFromContentDisposition(
          response.headers['content-disposition'],
          'evaluator-report.csv'
        );

      const downloadUrl =
        window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      try {
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }

    } catch (error) {
      console.error('CSV download failed:', error);
    } finally {
      if (isMountedRef.current) {
        setIsEvaluatorDownloading(false);
      }
    }
  },
  [csrfToken, isEvaluatorDownloading]
);

  // const handleApplyFilters = (
  //   reportType,
  //   payload
  // ) => {
  //   if (reportType === "idea") {
  //     downloadIdeaReport(payload);
  //     return;
  //   }
  //   if (reportType === "challenge") {
  //     downloadChallengeReport(payload);
  //   }
  // };

 // when value 3 select than call this 
 useEffect(() => {
  const {
    challengeStatus,
    startDate,
    endDate,
  } = challengeFilters;

  if (!challengeStatus?.length) {
    return;
  }

  // For ACTIVE status
  if (
    challengeStatus.length === 1 &&
    challengeStatus[0] === "ACTIVE"
  ) {
    requestGetChallengeWithStatusFilterFn(
      challengeFilters
    );
    return;
  }

  // For FINISHED or ALL
  if (startDate && endDate) {
    requestGetChallengeWithStatusFilterFn(
      challengeFilters
    );
  }
}, [challengeFilters]);

  /*
   * Component mount and unmount.
   *
   * These requests are intentionally executed once because they correspond
   * to the original componentDidMount behavior.
   */
  useEffect(() => {
    isMountedRef.current = true

    getIdeasSeries(ideasFromDate)
    getPrivateIdeasSeries(privateIdeasFromDate)
    getUserParticipation(userParticipationOffsetRef.current)
    getActiveOrganizations()
    getGroups()
    getActiveGoals()

    getActiveChallenges(
      ACTIVE_CHALLENGES,
      0,
      FIRST_LIMIT_CONSTANT,
      false,
      isHackathonEnable
    )

    getActiveTypes()

    return () => {
      isMountedRef.current = false
    }

    /*
     * These action creators are expected to have stable references.
     * Including every prop here could make initialization execute repeatedly.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * Refresh system accesses only when the domain creation date changes.
   */
  useEffect(() => {
    if (domainCreationDate) {
      getSystemAccesses(domainCreationDate)
    }
  }, [domainCreationDate, getSystemAccesses])

  /*
   * Load table data when the active tab changes, but only when that tab does
   * not already have rows.
   */
  useEffect(() => {
    switch (activeTab) {
      case dashboardTabs.IDEAS: {
        if (!ideas.tableData.dataRows.length) {
          const { page, rowsPerPage } = ideaTablePagination

          getIdeasComparisonTable(ideasFromDate, ideasToDate)
          getIdeas({
            limit: rowsPerPage,
            offset: page * rowsPerPage,
          })
        }
        break
      }

      case dashboardTabs.PEOPLE:
        if (!people.tableData.dataRows.length) {
          getPeopleComparisonTable(peopleFromDate, peopleToDate)
          getUsers(peopleUserFilter)
        }
        break

      case dashboardTabs.PRIVATE_IDEAS: {
        if (!privateIdeas.tableData.dataRows.length) {
          const { page, rowsPerPage } = privateIdeas.pagination

          getPrivateIdeasComparisonTable(
            privateIdeasFromDate,
            privateIdeasToDate
          )

          getPrivateIdeas({
            limit: rowsPerPage,
            offset: page * rowsPerPage,
          })
        }
        break
      }

      case dashboardTabs.ACTIVITY:
      default:
        if (!activity.tableData.dataRows.length) {
          getActivityComparisonTable(
            activityFromDate,
            activityToDate
          )
        }
    }
  }, [
    activeTab,
    activity.tableData.dataRows.length,
    ideas.tableData.dataRows.length,
    people.tableData.dataRows.length,
    privateIdeas.tableData.dataRows.length,
    privateIdeas.pagination,
    ideaTablePagination,
    peopleUserFilter,
    activityFromDate,
    activityToDate,
    ideasFromDate,
    ideasToDate,
    peopleFromDate,
    peopleToDate,
    privateIdeasFromDate,
    privateIdeasToDate,
    getActivityComparisonTable,
    getIdeasComparisonTable,
    getPeopleComparisonTable,
    getPrivateIdeasComparisonTable,
    getIdeas,
    getPrivateIdeas,
    getUsers,
  ])

  /*
   * Refresh each comparison table when its date range changes.
   *
   * The active-tab effect above handles the initial request. These effects
   * also support date changes while keeping each tab independent.
   */
  useEffect(() => {
    getActivityComparisonTable(activityFromDate, activityToDate)
  }, [
    activityFromDate,
    activityToDate,
    getActivityComparisonTable,
  ])

  useEffect(() => {
    getIdeasComparisonTable(ideasFromDate, ideasToDate)
  }, [
    ideasFromDate,
    ideasToDate,
    getIdeasComparisonTable,
  ])

  useEffect(() => {
    getPeopleComparisonTable(peopleFromDate, peopleToDate)
  }, [
    peopleFromDate,
    peopleToDate,
    getPeopleComparisonTable,
  ])

  useEffect(() => {
    getPrivateIdeasComparisonTable(
      privateIdeasFromDate,
      privateIdeasToDate
    )
  }, [
    privateIdeasFromDate,
    privateIdeasToDate,
    getPrivateIdeasComparisonTable,
  ])

  /*
   * Load the next participation page when a new series chunk arrives.
   */
  useEffect(() => {
    const currentFirstSeries = usersParticipation.series?.[0]
    const previousFirstSeries = previousParticipationSeriesRef.current

    if (
      usersParticipation.hasMore &&
      previousFirstSeries !== currentFirstSeries
    ) {
      userParticipationOffsetRef.current += USERS_LIMIT_CONSTANT

      getUserParticipation(userParticipationOffsetRef.current)
    }

    previousParticipationSeriesRef.current = currentFirstSeries
  }, [
    usersParticipation.hasMore,
    usersParticipation.series,
    getUserParticipation,
  ])

  return (
    <div className={classes.root}>
      <Container>
        <div className={classes.dashboad_secion}> 
        
         <div className="vf-challenge-filter-header">
          <h2
            id="evaluator-report-title"
            className="vf-challenge-filter-title"
          >
            {i18n.t('DASHBOARD.TITLE')}
          </h2>

          <p className="vf-challenge-filter-subtitle">
              Download Dashboard Reports for Ideas, Challenges, Challenge Tag Ideas, and Evaluator Reports
          </p>
        </div>
        </div>

      <div className={classes.reportContainer}>
        <ReportSelector
          onMainButtonAction={downloadCsv}
          isLoading={isDownloading}
        />
      </div>

      <FilterSection 
          onApply={downloadIdeaReport}
          isLoading={isIdeaDownloading}

      />

      <ChallengeFilterReport
        getChallengeList={getChallengeWithStatusFilterList}
        isChallengeLoading={getChallengeWithStatusFilterListIsLoading}
        onFilterChange={setChallengeFilters}
        onDownload={downloadChallengeReport}
        isDownloading={isChallengeLoading}
      />
      
      <EvaluatorReportFilter 
          onDownload={downloadEvaluatorReport}
          isLoading={isEvaluatorDownloading}
      />    




      <ChallengeAllReport
          onDownload={downloadChallengeAllCsv}
          isLoading={isChallengeAllReportDownloading}
      />


      <Paper className={classes.paper}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          aria-label={i18n.t('DASHBOARD.TITLE')}
          variant="fullWidth"
        >
          <Tab
            label={i18n.t('DASHBOARD.ACTIVITY')}
            value={dashboardTabs.ACTIVITY}
            disabled={isLoading}
          />

          <Tab
            label={i18n.t('DASHBOARD.IDEAS')}
            value={dashboardTabs.IDEAS}
            disabled={isLoading}
          />

          <Tab
            label={i18n.t('DASHBOARD.PEOPLE')}
            value={dashboardTabs.PEOPLE}
            disabled={isLoading}
          />

          {showPrivateTab && (
            <Tab
              label={i18n.t('DASHBOARD.PRIVATE_IDEAS')}
              value={dashboardTabs.PRIVATE_IDEAS}
              disabled={isLoading}
            />
          )}
        </Tabs>

        <div className={classes.dashboardContainer}>
          <IntervalDatesPicker
            onSelectFromDate={handleSelectFromDate}
            onSelectToDate={handleSelectToDate}
            fromDate={fromDate}
            toDate={toDate}
            disabled={isLoading}
            minDate={domainCreationDate}
          />

          <div
            className={clsx(
              activeTab !== dashboardTabs.ACTIVITY && classes.hide
            )}
          >
            <Activity
              tableData={activity.tableData}
              toDate={toDate}
              fromDate={fromDate}
              onChangeDates={handleDatesChange}
              isChartLoading={isActivitySeriesLoading}
              isTableLoading={isLoading}
              series={activity.chart.series}
              devicesInfo={activity.devicesInfo}
            />
          </div>

          <div
            className={clsx(
              activeTab !== dashboardTabs.IDEAS && classes.hide
            )}
          >
            <ActivityIdeas
              tableHeaders={comparisonIdeasTableHeaders}
              tableData={ideas.tableData}
              tableDataBottom={ideasTableBottom}
              tableDataBottomTotal={ideasTableBottomTotal}
              tableDataBottomIsLoading={ideasTableBottomIsLoading}
              toDate={toDate}
              fromDate={fromDate}
              onChangeDates={handleDatesChange}
              isChartLoading={isIdeasSeriesLoading}
              isTableLoading={isLoading}
              series={ideas.chart.series}
              navigateToIdea={navigateToIdea}
              organizations={organizations}
              groups={groups}
              strategics={strategics}
              challenges={challenges}
              types={types}
              statusFilterOptions={statusFilterOptions}
              onPageChange={handleIdeaPageChange}
              rowsPerPage={ideaTablePagination.rowsPerPage}
              page={ideaTablePagination.page}
            />
          </div>

          <div
            className={clsx(
              activeTab !== dashboardTabs.PEOPLE && classes.hide
            )}
          >
            <ActivityPeople
              tableData={people.tableData}
              isTableLoading={isLoading}
              tableUsersData={people.usersTable.users}
              totalSearchItems={people.usersTable.totalSearchItems}
              isTableUsersDataLoading={people.usersTable.isLoading}
              series={usersParticipation.series}
              totalUsers={usersParticipation.totalUsers}
              participationAvg={usersParticipation.participationAvg}
              successAvg={usersParticipation.successAvg}
              onViewAsClick={handleViewAsClick}
              navigateToUserProfile={navigateToUserProfile}
              onFilterByText={handleFilterByText}
              onFilterByType={handleFilterByType}
              isUsersParticipationSeriesLoading={
                isUsersParticipationSeriesLoading
              }
              onSendReportClick={onSendPeopleReport}
              onPageChange={handlePeoplePageChange}
              rowsPerPage={people.usersTable.filter.rowsPerPage}
              page={people.usersTable.filter.page}
            />
          </div>

          {showPrivateTab &&
            activeTab === dashboardTabs.PRIVATE_IDEAS && (
              <div>
                <ActivityIdeas
                  tableHeaders={comparisonPrivateIdeasTableHeaders}
                  tableData={privateIdeas.tableData}
                  tableDataBottom={privateIdeas.tableDataBottom.data}
                  tableDataBottomTotal={
                    privateIdeas.tableDataBottom.total
                  }
                  tableDataBottomIsLoading={
                    privateIdeas.tableDataBottom.ideasLoading
                  }
                  toDate={toDate}
                  fromDate={fromDate}
                  onChangeDates={handleDatesChange}
                  isChartLoading={isPrivateIdeasLoading}
                  isTableLoading={isLoading}
                  series={privateIdeas.chart.series}
                  navigateToIdea={navigateToIdea}
                  organizations={organizations}
                  groups={groups}
                  strategics={strategics}
                  statusFilterOptions={privateStatusFilterOptions}
                  onPageChange={handlePrivateIdeaPageChange}
                  rowsPerPage={privateIdeas.pagination.rowsPerPage}
                  page={privateIdeas.pagination.page}
                  isPrivate
                />
              </div>
            )}
        </div>
      </Paper>

      </Container>
    </div>
  )
}


Dashboard.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string,
    titleContainer: PropTypes.string,
    reportContainer: PropTypes.string,
    paper: PropTypes.string,
    dashboardContainer: PropTypes.string,
    hide: PropTypes.string,
  }).isRequired,

  activeTab: PropTypes.string.isRequired,
  showPrivateTab: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isActivitySeriesLoading: PropTypes.bool.isRequired,
  isIdeasSeriesLoading: PropTypes.bool.isRequired,
  isPrivateIdeasLoading: PropTypes.bool.isRequired,
  isUsersParticipationSeriesLoading: PropTypes.bool.isRequired,
  isHackathonEnable: PropTypes.bool,

  activityFromDate: PropTypes.instanceOf(Date).isRequired,
  activityToDate: PropTypes.instanceOf(Date).isRequired,
  ideasFromDate: PropTypes.instanceOf(Date).isRequired,
  ideasToDate: PropTypes.instanceOf(Date).isRequired,
  peopleFromDate: PropTypes.instanceOf(Date).isRequired,
  peopleToDate: PropTypes.instanceOf(Date).isRequired,
  privateIdeasFromDate: PropTypes.instanceOf(Date).isRequired,
  privateIdeasToDate: PropTypes.instanceOf(Date).isRequired,
  domainCreationDate: PropTypes.instanceOf(Date),

  csrfToken: PropTypes.string.isRequired,

  activity: PropTypes.shape({
    tableData: PropTypes.shape({
      dataRows: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    chart: PropTypes.shape({
      series: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    devicesInfo: PropTypes.shape({}),
  }).isRequired,

  ideas: PropTypes.shape({
    tableData: PropTypes.shape({
      dataRows: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    chart: PropTypes.shape({
      series: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,

  people: PropTypes.shape({
    tableData: PropTypes.shape({
      dataRows: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    usersTable: PropTypes.shape({
      users: PropTypes.arrayOf(PropTypes.shape({})),
      totalSearchItems: PropTypes.number,
      isLoading: PropTypes.bool,
      filter: PropTypes.shape({
        page: PropTypes.number,
        rowsPerPage: PropTypes.number,
      }),
    }),
  }).isRequired,

  privateIdeas: PropTypes.shape({
    tableData: PropTypes.shape({
      dataRows: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    tableDataBottom: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
      total: PropTypes.number,
      ideasLoading: PropTypes.bool,
    }),
    chart: PropTypes.shape({
      series: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    pagination: PropTypes.shape({
      page: PropTypes.number,
      rowsPerPage: PropTypes.number,
    }),
  }).isRequired,

  peopleUserFilter: PropTypes.shape({
    name: PropTypes.string,
    filterType: PropTypes.string,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
  }).isRequired,

  userTableFilter: PropTypes.shape({
    name: PropTypes.string,
    filterType: PropTypes.string,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
  }).isRequired,

  ideaTablePagination: PropTypes.shape({
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
  }).isRequired,

  usersParticipation: PropTypes.shape({
    hasMore: PropTypes.bool,
    totalUsers: PropTypes.number,
    participationAvg: PropTypes.string,
    successAvg: PropTypes.string,
    series: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,

  ideasTableBottom: PropTypes.arrayOf(PropTypes.shape({})),
  ideasTableBottomTotal: PropTypes.number,
  ideasTableBottomIsLoading: PropTypes.bool,

  organizations: PropTypes.arrayOf(PropTypes.shape({})),
  groups: PropTypes.arrayOf(PropTypes.shape({})),
  strategics: PropTypes.arrayOf(PropTypes.shape({})),
  challenges: PropTypes.arrayOf(PropTypes.shape({})),
  types: PropTypes.arrayOf(PropTypes.shape({})),

  onChangeTab: PropTypes.func.isRequired,
  onChangeDate: PropTypes.func.isRequired,
  navigateToUserProfile: PropTypes.func.isRequired,
  navigateToIdea: PropTypes.func.isRequired,
  onSendPeopleReport: PropTypes.func.isRequired,

  getActivityComparisonTable: PropTypes.func.isRequired,
  getIdeasComparisonTable: PropTypes.func.isRequired,
  getPeopleComparisonTable: PropTypes.func.isRequired,
  getPrivateIdeasComparisonTable: PropTypes.func.isRequired,
  getSystemAccesses: PropTypes.func.isRequired,
  getIdeasSeries: PropTypes.func.isRequired,
  getPrivateIdeasSeries: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  getUserParticipation: PropTypes.func.isRequired,
  getPrivateIdeas: PropTypes.func.isRequired,
  getIdeas: PropTypes.func.isRequired,
  getActiveOrganizations: PropTypes.func.isRequired,
  getGroups: PropTypes.func.isRequired,
  getActiveGoals: PropTypes.func.isRequired,
  getActiveChallenges: PropTypes.func.isRequired,
  getActiveTypes: PropTypes.func.isRequired,

  setUsersTableFilter: PropTypes.func.isRequired,
  setPrivateIdeaTableFilter: PropTypes.func.isRequired,
  setIdeaTableFilter: PropTypes.func.isRequired,
}

Dashboard.defaultProps = {
  domainCreationDate: null,
  isHackathonEnable: false,
  ideasTableBottom: [],
  ideasTableBottomTotal: 0,
  ideasTableBottomIsLoading: false,
  organizations: [],
  groups: [],
  strategics: [],
  challenges: [],
  types: [],
}

export default withStyles(styles)(memo(Dashboard))